'use client'

import * as React from 'react'
import { FileQuestion, Info } from 'lucide-react'

import {
  Alert,
  AlertDescription,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { formatDate, getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { Tag } from '../../components/tag'

export interface MissionPageSkillWeight {
  name: string
  iconUrl: string | null
  totalWeight: number
}

export interface MissionPageData {
  id: string
  type: 'challenge' | 'course' | 'lessonModule'
  title: string
  coverUrl: string | null
  backgroundImageUrl: string | null
  startAt?: Date | null
  endAt?: Date | null
  learningFocus: string | null
  totalXP: number
  skillWeights: MissionPageSkillWeight[]
  grade: { id: string; name: string; iconUrl?: string | null } | null
  curricula: { curriculumId: string; curriculum: { name: string; iconUrl?: string | null } }[]
  contributors: {
    contributorId: string
    contributor: { name: string; avatarUrl?: string | null; iconUrl?: string | null }
  }[]
  host?: {
    avatarUrl: string | null
    name: string
  }
}

interface MissionPageProps {
  data: MissionPageData
  onAccept?: () => void
  actionLabel?: string
  actionVariant?: 'gradient' | 'primary'
  alertMessage?: string
  resultDialogOpen?: boolean
  onResultDialogOpenChange?: (open: boolean) => void
  onTagClick?: (type: 'grade' | 'curriculum' | 'contributor', id: string) => void
}

export function MissionPage({
  data,
  onAccept,
  actionLabel = 'Terima tantangan',
  actionVariant = 'gradient',
  alertMessage,
  resultDialogOpen,
  onResultDialogOpenChange,
  onTagClick,
}: MissionPageProps) {
  const grade = data?.grade
  const gradeName = grade
    ? grade.name.toLowerCase().startsWith('kelas')
      ? grade.name.charAt(0).toUpperCase() + grade.name.slice(1).toLowerCase()
      : grade.name
    : null

  return (
    <div
      className='srd:flex srd:items-start srd:justify-center srd:p-4 srd:md:p-8 srd:min-h-full srd:bg-cover srd:bg-center srd:max-h-[calc(100dvh-var(--navbar-height,0px))] srd:h-[calc(100dvh-var(--navbar-height,0px))] srd:overflow-y-auto srd:w-full'
      style={data?.backgroundImageUrl ? { backgroundImage: `url(${data.backgroundImageUrl})` } : undefined}
    >
      <div className='srd:bg-neutral-white srd:rounded-2xl srd:overflow-hidden srd:w-full srd:max-w-[800px] srd:shadow-lg'>
        <div className='srd:flex srd:flex-col srd:md:flex-row'>
          {/* Left: cover + XP */}
          <div className='srd:flex srd:flex-col srd:gap-y-4 srd:p-6 srd:md:shrink-0 srd:mx-auto'>
            <div className='srd:rounded-2xl srd:overflow-hidden srd:w-full srd:aspect-4/3 srd:max-w-[320px] srd:bg-grey-10 srd:flex srd:items-center srd:justify-center'>
              {data.coverUrl ? (
                <img
                  src={data.coverUrl}
                  alt={data.title}
                  className='srd:w-full srd:h-full srd:object-cover'
                  draggable={false}
                />
              ) : (
                <FileQuestion className='srd:w-10 srd:h-10 srd:text-grey-40' />
              )}
            </div>

            <XPSection skillWeights={data.skillWeights} />
          </div>

          {/* Right: metadata */}
          <div className='srd:flex srd:flex-col srd:gap-y-2 srd:p-6 srd:flex-1 srd:min-w-0 srd:md:pt-6 srd:pt-0'>
            <div className='srd:flex srd:flex-col srd:gap-y-1'>
              <p className={cn(typographyVariants({ variant: 'label-lg' }), 'srd:text-grey-100')}>
                Misi {data.type === 'challenge' ? 'tantangan' : data.type === 'course' ? 'belajar' : 'latihan'}
              </p>

              <h2 className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-black')}>
                {data.title}
              </h2>

              {data.startAt && data.endAt && (
                <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
                  {formatDate(new Date(data.startAt), { dateStyle: 'long', timeStyle: 'short' })} -{' '}
                  {formatDate(new Date(data.endAt), { dateStyle: 'long', timeStyle: 'short' })}
                </p>
              )}
            </div>

            {data.host && (
              <div className='srd:flex srd:flex-col srd:gap-y-1'>
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
                  Guru pendamping:
                </p>

                <div className='srd:flex srd:items-center srd:gap-x-2'>
                  <Avatar profile='user' className='h-8 w-8'>
                    <AvatarImage src={data.host.avatarUrl ?? undefined} alt='Profile Picture' />
                    <AvatarFallback className='text-xs'>{getInitials(data.host.name)}</AvatarFallback>
                  </Avatar>

                  <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
                    {data.host.name}
                  </p>
                </div>
              </div>
            )}

            {data.learningFocus && (
              <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
                {data.learningFocus}
              </p>
            )}

            {onTagClick && (
              <div className='srd:flex srd:flex-wrap srd:gap-x-2 srd:gap-y-1.5 srd:mt-1'>
                {grade && (
                  <Tag
                    label={gradeName ?? grade.name}
                    onClick={() => onTagClick('grade', grade.id)}
                    iconUrl={grade.iconUrl ?? undefined}
                  />
                )}
                {data.curricula.map((c) => (
                  <Tag
                    key={c.curriculumId}
                    label={c.curriculum.name}
                    onClick={() => onTagClick('curriculum', c.curriculumId)}
                    iconUrl={c.curriculum.iconUrl ?? undefined}
                  />
                ))}
                {data.contributors.map((c) => (
                  <Tag
                    key={c.contributorId}
                    label={c.contributor.name}
                    onClick={() => onTagClick('contributor', c.contributorId)}
                    iconUrl={c.contributor.iconUrl ?? c.contributor.avatarUrl ?? undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {(onAccept ?? alertMessage) && (
          <div className='srd:flex srd:items-center srd:justify-end srd:py-6 srd:border-t srd:border-outline-variant/50 srd:px-4 srd:gap-3'>
            {alertMessage && (
              <Alert variant='neutral' className='srd:flex-1'>
                <Info />
                <AlertDescription>{alertMessage}</AlertDescription>
              </Alert>
            )}
            {onAccept && (
              <Button variant={actionVariant} onClick={onAccept}>
                {actionLabel}
              </Button>
            )}
          </div>
        )}
      </div>

      {onResultDialogOpenChange && (
        <Dialog open={resultDialogOpen ?? false} onOpenChange={onResultDialogOpenChange}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Hasil</DialogTitle>
            </DialogHeader>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
              Kamu sudah menyelesaikan misi ini.
            </p>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

interface XPSectionProps {
  skillWeights: MissionPageSkillWeight[]
}

function XPSection({ skillWeights }: XPSectionProps) {
  return (
    <div className='srd:flex srd:flex-col srd:gap-y-2'>
      <div className='srd:flex srd:flex-col srd:gap-y-1 srd:w-full'>
        {skillWeights.map((weight) => (
          <div key={weight.name} className='srd:w-full srd:flex srd:items-center srd:justify-between srd:gap-x-4'>
            <div className='srd:flex srd:items-start srd:flex-1'>
              {weight.iconUrl ? (
                <div className='srd:size-6 srd:rounded-md srd:overflow-clip srd:mr-2 srd:shrink-0'>
                  <img
                    src={weight.iconUrl}
                    alt={weight.name}
                    className='srd:size-6 srd:object-cover'
                    draggable={false}
                    width={24}
                    height={24}
                  />
                </div>
              ) : (
                <div className='srd:size-6 srd:rounded-md srd:overflow-clip srd:bg-grey-20 srd:flex srd:items-center srd:justify-center srd:mr-2 srd:shrink-0'>
                  <FileQuestion className='srd:size-4 srd:text-grey-60' />
                </div>
              )}
              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:flex-1')}>{weight.name}</p>
            </div>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:font-bold')}>
              +{Math.round(weight.totalWeight)}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
